import React, { useEffect, useState, useRef } from "react";
import { Send, Inbox, CheckCircle2, FileText } from "lucide-react";
import { api } from "@/lib/apiClient";
import { PageHeader, Loader, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";

export default function AdminMessages() {
  const [convs, setConvs] = useState(null);
  const [status, setStatus] = useState("all");
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tOpen, setTOpen] = useState(false);
  const [newT, setNewT] = useState({ category: "General", title: "", body: "" });
  const bottomRef = useRef();

  const load = async () => { const { data } = await api.get(`/admin/conversations${status !== "all" ? `?status=${status}` : ""}`); setConvs(data); };
  const loadTemplates = async () => setTemplates((await api.get("/admin/templates")).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { loadTemplates(); }, []);
  useEffect(() => { if (active) api.get(`/conversations/${active}/messages`).then((r) => setThread(r.data)); }, [active]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!text.trim()) return;
    await api.post(`/conversations/${active}/messages`, { body: text }); setText("");
    setThread((await api.get(`/conversations/${active}/messages`)).data); load();
  };
  const setConvStatus = async (s) => { await api.patch(`/admin/conversations/${active}`, { status: s }); load(); setThread((t) => ({ ...t, conversation: { ...t.conversation, status: s } })); toast.success("Status updated"); };
  const saveTemplate = async () => { await api.post("/admin/templates", newT); setNewT({ category: "General", title: "", body: "" }); setTOpen(false); loadTemplates(); toast.success("Template saved"); };
  const delTemplate = async (id) => { await api.delete(`/admin/templates/${id}`); loadTemplates(); };

  return (
    <div>
      <PageHeader title="Support Inbox" subtitle="Reply to student inquiries"
        action={
          <div className="flex gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-36" data-testid="conv-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem><SelectItem value="open">Open</SelectItem>
                <SelectItem value="pending">Pending</SelectItem><SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            <Dialog open={tOpen} onOpenChange={setTOpen}>
              <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="manage-templates"><FileText className="w-4 h-4 mr-1.5" /> Templates</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Saved Response Templates</DialogTitle></DialogHeader>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {templates.map((t) => <div key={t.id} className="flex items-center justify-between text-sm p-2 rounded-lg bg-secondary/50"><span>[{t.category}] {t.title}</span><button onClick={() => delTemplate(t.id)} className="text-red-500 text-xs">Delete</button></div>)}
                  {templates.length === 0 && <p className="text-sm text-muted-foreground">No templates yet.</p>}
                </div>
                <div className="space-y-2 border-t border-border pt-3">
                  <Input placeholder="Category (e.g. Course Question)" value={newT.category} onChange={(e) => setNewT({ ...newT, category: e.target.value })} data-testid="t-category" />
                  <Input placeholder="Title" value={newT.title} onChange={(e) => setNewT({ ...newT, title: e.target.value })} data-testid="t-title" />
                  <Textarea placeholder="Response body" value={newT.body} onChange={(e) => setNewT({ ...newT, body: e.target.value })} data-testid="t-body" />
                </div>
                <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={saveTemplate} data-testid="save-template">Save Template</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>} />

      <div className="grid md:grid-cols-[320px_1fr] gap-4">
        <div className="space-y-2">
          {!convs ? <Loader /> : convs.length === 0 ? <EmptyState icon={Inbox} title="No conversations" testId="admin-convs-empty" /> :
            convs.map((c) => (
              <button key={c.id} onClick={() => setActive(c.id)} className={`w-full text-left bg-white border rounded-2xl p-3 shadow-sm transition-colors ${active === c.id ? "border-teal" : "border-border hover:bg-secondary/40"}`} data-testid={`admin-conv-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-sm truncate">{c.student_name}</p>
                  {c.unread_admin && <span className="w-2 h-2 rounded-full bg-teal shrink-0" />}
                </div>
                <p className="text-xs text-muted-foreground truncate">{c.subject}</p>
                <div className="mt-1"><StatusBadge status={c.status} /></div>
              </button>
            ))}
        </div>

        <div className="bg-white border border-border rounded-2xl shadow-sm flex flex-col h-[70vh]">
          {!thread ? <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div> : (
            <>
              <div className="p-4 border-b border-border flex items-center justify-between gap-2 flex-wrap">
                <div><p className="font-semibold">{thread.conversation.subject}</p><p className="text-xs text-muted-foreground">{thread.conversation.student_email}</p></div>
                <div className="flex gap-1">
                  {["open", "pending", "resolved"].map((s) => (
                    <Button key={s} size="sm" variant={thread.conversation.status === s ? "default" : "outline"} className="rounded-full text-xs h-7" onClick={() => setConvStatus(s)} data-testid={`set-${s}`}>{s}</Button>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-thread">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_role !== "student" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${m.sender_role !== "student" ? "bg-teal text-white rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}>
                      <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                      <p className={`text-[10px] mt-1 ${m.sender_role !== "student" ? "text-white/70" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div className="p-3 border-t border-border space-y-2">
                {templates.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild><Button size="sm" variant="ghost" className="text-xs text-teal" data-testid="use-template">Insert template</Button></PopoverTrigger>
                    <PopoverContent className="w-72 max-h-60 overflow-y-auto p-2">
                      {templates.map((t) => <button key={t.id} onClick={() => setText(t.body)} className="w-full text-left p-2 rounded-lg hover:bg-secondary text-sm">[{t.category}] {t.title}</button>)}
                    </PopoverContent>
                  </Popover>
                )}
                <div className="flex gap-2">
                  <Input placeholder="Type a reply..." value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="admin-reply-input" />
                  <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={send} data-testid="admin-send"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
